import { Dispatch } from 'redux'

import { Response } from 'cross-fetch'

import {
  FilteractionObject,
  ProjectFields,
  YesObject
} from '@yes.technology/react-toolkit'

import { ActivityValue, FlatInteractionModelData } from 'types/shared'

import { resolveQuery } from '../modules/filteraction/FilteractionResolver'

import apiAsync from './api-async'

import {
  FetchYesObjectsResponse,
  FilteractionParams,
  GenericAPIResponse,
  GetInteractionModelResponse,
  PostSignedUploadResponse,
  ReplaceFilteractionParamsParams
} from './apiTypes'
import { getProperty } from './helpers/object'
import fetchFilteraction from 'modules/filteraction/fetchFilteraction'

class Api {
  private dispatch!: Dispatch

  setDispatch(dispatch: Dispatch) {
    this.dispatch = dispatch
  }

  postInputValidation(
    obj: unknown,
    endpoint: string,
    showLoading: boolean
  ): Promise<GenericAPIResponse> {
    return apiAsync.requestSync({
      endpoint,
      method: 'POST',
      obj,
      showLoading,
      customEndpoint: true
    })
  }

  async getAutocomplete(
    value: string,
    endpoint: string,
    showLoading: boolean,
    filteraction?: string,
    filteractionParams?: FilteractionParams[],
    activityValues?: FlatInteractionModelData['activity_values'],
    parentPath?: string[],
    throwError?: boolean,
    projectFields?: ProjectFields
  ): Promise<FetchYesObjectsResponse> {
    if (filteraction) {
      return await this.getAutocompleteWithFilteraction(
        value,
        filteraction,
        showLoading,
        filteractionParams,
        activityValues,
        parentPath,
        throwError,
        projectFields
      )
    }

    const response = await apiAsync.requestSync({
      endpoint: `${endpoint}?s=${encodeURIComponent(value)}`,
      method: 'GET',
      showLoading,
      customEndpoint: true
    })

    return {
      metadata: response.data?.metadata?.object_class,
      objects: response.data?.objects || response.data,
      pagination: response.data?.pagination,
      message: response.message
    }
  }

  replaceFilteractionParams({
    filteractionParams,
    activityValues,
    filteraction,
    parentPath
  }: ReplaceFilteractionParamsParams) {
    filteractionParams.forEach((param) => {
      let activityValue: ActivityValue | undefined

      if (parentPath.length > 0) {
        activityValue = getProperty(activityValues, [
          ...parentPath,
          param.input_value_activity_id
        ])
      }

      if (
        param.input_value_activity_id in activityValues &&
        !Array.isArray(activityValues[param.input_value_activity_id])
      ) {
        activityValue = activityValues[
          param.input_value_activity_id
        ] as ActivityValue
      }

      if (activityValue) {
        const value =
          typeof activityValue === 'object'
            ? activityValue.id || activityValue.des
            : activityValue

        filteraction.query = filteraction.query.replace(
          param.query_param_name,
          value
        )
      }
    })
  }

  async getAutocompleteWithFilteraction(
    value: string,
    filteractionId: string,
    showLoading: boolean,
    filteractionParams?: FilteractionParams[],
    activityValues?: FlatInteractionModelData['activity_values'],
    parentPath?: string[],
    throwError?: boolean,
    projectFields?: ProjectFields
  ): Promise<FetchYesObjectsResponse> {
    const onProgressCallback = () => undefined

    const filteraction = await fetchFilteraction(
      filteractionId,
      showLoading,
      throwError
    )

    if (
      filteractionParams &&
      filteraction &&
      activityValues &&
      Object.keys(activityValues).length > 0
    ) {
      // percorre todos os steps para encontrar activities com o mesmo UUID dos parâmetros da filteraction e executar replace das strings
      // todo: talvez no futuro aqui entre uma validação para activity_group
      this.replaceFilteractionParams({
        filteractionParams,
        activityValues,
        filteraction: filteraction as FilteractionObject,
        parentPath: parentPath || []
      })
    }

    const query =
      filteraction?.query && typeof filteraction.query === 'string'
        ? JSON.parse(filteraction.query)
        : {}

    const objectclassId = (
      filteraction?.filter_action_objectclass as YesObject[]
    )?.[0]?.uuid

    const filteractionResolvedQuery = await resolveQuery(
      query,
      onProgressCallback,
      [filteractionId],
      showLoading
    )

    // Agora juntamos os IDs retornados pela filteraction com
    // a descrição digitada no autocomplete
    const autocompleteQuery = {
      $and: [
        filteractionResolvedQuery,
        {
          'des.des': { $regex: value }
        }
      ]
    }

    const filterBody = {
      query: autocompleteQuery,
      objectclass: objectclassId,
      orderby: [
        {
          'des.des': 'asc'
        }
      ],
      pagination: {
        offset: 0,
        limit: 100
      },
      visualization: 'classic-list',
      skip_relations: true,
      skip_metadata: true,
      project_fields: projectFields || { uuid: 1, des: 1 }
    }

    const results = this.postObjectsFilter(filterBody, showLoading, throwError)

    return results
  }

  async postObjectsFilter(
    obj: unknown,
    showLoading: boolean,
    throwError: boolean = true
  ): Promise<FetchYesObjectsResponse> {
    const response = await apiAsync.requestSync({
      endpoint: 'objects_filter',
      method: 'POST',
      obj,
      showLoading,
      throwError
    })

    return {
      metadata: response.data?.metadata?.object_class,
      objects: response.data?.objects,
      pagination: response.data?.pagination
    }
  }

  postReloadToken(obj: unknown, showLoading?: boolean) {
    return apiAsync.requestSync({
      endpoint: 'reload-token',
      method: 'POST',
      obj,
      showLoading
    })
  }

  async postSaveInteraction(obj: unknown, showLoading: boolean) {
    return apiAsync
      .requestSync({
        endpoint: 'interactions',
        method: 'POST',
        obj,
        showLoading,
        throwError: true
      })
      .catch((error: unknown) => {
        if (error instanceof Response) {
          this.dispatch({ type: 'api/ERROR', data: { status: error.status } })

          throw error
        }
      })
  }

  async patchSaveInteraction(obj: unknown, showLoading: boolean) {
    return apiAsync
      .requestSync({
        endpoint: 'interactions',
        method: 'PATCH',
        obj,
        showLoading,
        throwError: true
      })
      .catch((error: unknown) => {
        if (error instanceof Response) {
          this.dispatch({ type: 'api/ERROR', data: { status: error.status } })

          throw error
        }
      })
  }

  async getInteractionModels(
    idInteractionModel: string,
    showLoading: boolean
  ): Promise<GetInteractionModelResponse> {
    return apiAsync
      .requestSync({
        endpoint: 'interaction-models',
        method: 'GET',
        path: '/' + idInteractionModel,
        showLoading
      })
      .then((response: GenericAPIResponse) => {
        if (response?.success && response?.data) {
          return response
        }

        throw new Error(response?.message)
      })
      .catch((e: unknown) => {
        throw new Error(e instanceof Error ? e.message : 'Erro desconhecido')
      })
  }

  async patchObjects<T extends YesObject>(
    obj: Partial<T>,
    uuid: string,
    showLoading: boolean
  ): Promise<YesObject> {
    const response = await apiAsync
      .requestSync({
        endpoint: 'objects',
        method: 'PATCH',
        path: '/' + uuid,
        obj,
        showLoading,
        throwError: true
      })
      .catch((error: unknown) => {
        if (error instanceof Response) {
          this.dispatch({ type: 'api/ERROR', data: { status: error.status } })
        }
      })

    return response.data
  }

  async postObjects<T extends YesObject>(
    obj: Partial<T>,
    showLoading: boolean
  ): Promise<YesObject> {
    const response = await apiAsync
      .requestSync({
        endpoint: 'objects',
        method: 'POST',
        obj,
        showLoading,
        throwError: true
      })
      .catch((error: unknown) => {
        if (error instanceof Response) {
          this.dispatch({ type: 'api/ERROR', data: { status: error.status } })
        }
      })

    return response.data
  }

  postSignedUploadUrl(
    obj: { filename: string; content_type: string },
    showLoading: boolean
  ): Promise<PostSignedUploadResponse> {
    return apiAsync.requestSync({
      endpoint: 'signed-upload-url',
      method: 'POST',
      obj,
      showLoading
    })
  }
}

export default new Api()
